import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { CloudSun, ArrowRight, BarChart3, Shield, Zap, Globe, Loader2 } from 'lucide-react';

const Login = () => {
  const navigate = useNavigate();
  const { login, isAuthenticated, loading } = useAuth();
  const [isLoading, setIsLoading] = useState(false);

  // Already signed in (e.g. by auto-login): skip this page.
  useEffect(() => {
    if (isAuthenticated && !loading) navigate('/', { replace: true });
  }, [isAuthenticated, loading, navigate]);

  const handleDemoLogin = async () => {
    setIsLoading(true);
    try {
      await login('demo@blueskies.com', 'password123');
      navigate('/', { replace: true });
    } catch (err) {
      console.error('Login failed:', err);
    } finally {
      setIsLoading(false);
    }
  };

  const features = [
    { icon: BarChart3, title: 'Real-time Analytics', desc: 'Track your finances with live dashboards' },
    { icon: Shield, title: 'Bank-grade Security', desc: 'Enterprise-level protection for your data' },
    { icon: Zap, title: 'AI-Powered Insights', desc: 'Smart automation and predictions' },
    { icon: Globe, title: 'Multi-Currency', desc: 'Support for international operations' },
  ];

  return (
    <div className="min-h-screen flex">
      {/* Left Panel - Branding */}
      <div className="hidden lg:flex lg:w-1/2 bg-gradient-to-br from-sky-600 via-sky-700 to-indigo-800 p-12 flex-col justify-between relative overflow-hidden">
        <div className="absolute inset-0 bg-[url('data:image/svg+xml,...')] opacity-10"></div>
        
        <div className="relative z-10">
          <div className="flex items-center gap-3 mb-2">
            <div className="p-2 bg-white/20 rounded-xl backdrop-blur-sm">
              <CloudSun className="w-8 h-8 text-white" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-white">AI Blue Skies</h1>
              <p className="text-sky-200 text-sm">Cloud Accounting</p>
            </div>
          </div>
        </div>

        <div className="relative z-10 space-y-8">
          <div>
            <h2 className="text-4xl font-bold text-white mb-4">
              Professional Accounting,<br />Powered by AI
            </h2>
            <p className="text-sky-100 text-lg">
              Streamline your financial operations with intelligent automation and real-time insights.
            </p>
          </div>

          <div className="grid grid-cols-2 gap-4">
            {features.map((feature, idx) => (
              <div key={idx} className="p-4 bg-white/10 rounded-xl backdrop-blur-sm border border-white/20">
                <feature.icon className="w-6 h-6 text-sky-300 mb-2" />
                <h3 className="text-white font-semibold">{feature.title}</h3>
                <p className="text-sky-200 text-sm">{feature.desc}</p>
              </div>
            ))}
          </div>
        </div>

        <div className="relative z-10 text-sky-200 text-sm">
          © 2026 Blue Skies Cloud. Enterprise accounting solutions.
        </div>
      </div>

      {/* Right Panel - Login */}
      <div className="w-full lg:w-1/2 flex items-center justify-center p-8 bg-slate-50">
        <div className="w-full max-w-md">
          {/* Mobile Logo */}
          <div className="lg:hidden flex items-center gap-3 mb-8 justify-center">
            <div className="p-2 bg-sky-100 rounded-xl">
              <CloudSun className="w-8 h-8 text-sky-600" />
            </div>
            <div>
              <h1 className="text-xl font-bold text-slate-900">AI Blue Skies</h1>
              <p className="text-slate-500 text-sm">Cloud Accounting</p>
            </div>
          </div>

          <div className="text-center mb-8">
            <h2 className="text-2xl font-bold text-slate-900">Welcome Back</h2>
            <p className="text-slate-500 mt-2">Access your accounting dashboard</p>
          </div>

          <Button 
            onClick={handleDemoLogin}
            disabled={isLoading}
            className="w-full h-14 bg-sky-600 hover:bg-sky-700 text-white font-medium text-lg"
            data-testid="demo-login-btn"
          >
            {isLoading ? (
              <span className="flex items-center gap-2">
                <Loader2 className="w-5 h-5 animate-spin" />
                Signing in...
              </span>
            ) : (
              <span className="flex items-center justify-center gap-2">
                Enter Demo Account
                <ArrowRight className="w-5 h-5" />
              </span>
            )}
          </Button>

          <p className="mt-6 text-center text-sm text-slate-500">
            Click above to access the demo accounting system
          </p>

          <p className="mt-8 text-center text-sm text-slate-500">
            By continuing, you agree to our{' '}
            <a href="#" className="text-sky-600 hover:underline">Terms of Service</a>
            {' '}and{' '}
            <a href="#" className="text-sky-600 hover:underline">Privacy Policy</a>
          </p>
        </div>
      </div>
    </div>
  );
};

export default Login;
